import React, { useState, useEffect } from 'react';
import { Snackbar } from '@mui/material';
import Alert from 'domains/core/components/Alert';

type DashboardDownloadSnackbarProps = {
    isDownloading: boolean;
    isSuccess: boolean;
    error: Error | null;
    dashboardName: string;
    onSuccessClosed?: () => void;
    onErrorClosed?: () => void;
};

const DashboardDownloadSnackbar: React.FC<DashboardDownloadSnackbarProps> = ({
    isDownloading,
    isSuccess,
    error,
    dashboardName,
    onSuccessClosed,
    onErrorClosed,
}) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<'success' | 'info' | 'error' | 'warning'>('info');
    const [autoHideDuration, setAutoHideDuration] = useState<number | null>(6000);

    useEffect(() => {
        if (isDownloading) {
            setMessage(`Downloading ${dashboardName}...`);
            setSeverity('info');
            setAutoHideDuration(null);
            setOpen(true);
        } else if (isSuccess) {
            setMessage(`${dashboardName} successfully downloaded`);
            setSeverity('success');
            setAutoHideDuration(6000);
            setOpen(true);
        } else if (error) {
            setMessage(`Failed to download ${dashboardName}`);
            setSeverity('error');
            setAutoHideDuration(6000);
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [isDownloading, isSuccess, error, dashboardName]);

    const handleClose = () => {
        setOpen(false);

        // Call appropriate callback based on the current state
        if (isSuccess && onSuccessClosed) {
            onSuccessClosed();
        } else if (error && onErrorClosed) {
            onErrorClosed();
        }
    };

    return (
        <Snackbar
            open={open}
            autoHideDuration={autoHideDuration}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert onClose={handleClose} severity={severity} variant="standard">
                {message}
            </Alert>
        </Snackbar>
    );
};

export default DashboardDownloadSnackbar;
